// Deliberate duplicate of town-square/src/components/arty/ArtyFullScreenSheet.tsx (ADR 0002). A change here belongs there too.
// Client divergence: the shell is @artstorefronts/ui's Dialog, not a hand-rolled portal. The
// flyout underneath is a modal Radix Sheet: a non-Radix overlay on the body counts as a click
// outside it (closing the flyout) and inherits its pointer-events lock. As a Radix layer the
// sheet shares the flyout's layer stack (one @radix-ui/react-dismissable-layer in
// node_modules), so one Escape closes only the top layer, and Radix owns focus trap, focus
// return and scroll lock. The backdrop and the X live INSIDE the content, which is itself
// the full-screen layer, so a backdrop click is ours to handle rather than Radix's "outside".
import { type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@artstorefronts/ui';

/**
 * The library's centered-card defaults, undone (tailwind-merge drops each one): the content
 * is the whole viewport, transparent, with the panel drawn inside it.
 */
const FULL_SCREEN =
  'fixed inset-0 top-0 left-0 block h-dvh w-screen max-w-none translate-x-0 translate-y-0 gap-0 rounded-none bg-transparent p-0 ring-0 sm:max-w-none';

/**
 * A near-full-screen modal for image actions (the "everything you can do"
 * sheet). The big black X sits on the backdrop layer, not inside the
 * scrolling body, so it stays put while the content scrolls.
 */
export function ArtyFullScreenSheet({
  open,
  onClose,
  ariaLabel,
  children,
}: {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  const { t } = useTranslation('arty-card');

  return (
    <Dialog
      onOpenChange={(next) => {
        if (next) {
          return;
        }
        onClose();
      }}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className={FULL_SCREEN}
        onKeyDown={(event) => {
          // Radix hears Escape on the document; this only keeps it out of the flyout's React tree.
          if (event.key === 'Escape') {
            event.stopPropagation();
          }
        }}
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{ariaLabel}</DialogTitle>
        <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={onClose} />
        <button
          aria-label={t('sheet.close')}
          className="fixed right-4 top-4 z-[102] flex size-11 items-center justify-center rounded-full bg-black text-white shadow-lg transition hover:bg-black/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 sm:right-[calc(4vw+0.75rem)] sm:top-[calc(3vh+0.75rem)]"
          onClick={onClose}
          type="button"
        >
          <X className="size-6" />
        </button>
        <div className="absolute inset-2 overflow-hidden rounded-2xl bg-white shadow-2xl sm:inset-x-[4vw] sm:inset-y-[3vh]">
          <div className="h-full overflow-y-auto">{children}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
